/**
 * useOrganizationSchema — Génère le JSON-LD LocalBusiness/NGO enrichi pour
 * la page /associations/[slug]. Karim 2026-06-15 (SEO sprint v4).
 *
 * Mapping catégorie Donoor → sous-type Schema.org le plus spécifique
 * possible. Google récompense la précision (ex: SportsClub > Organization
 * pour un club de foot).
 *
 * Champs enrichis (par rapport à NGO basique pré-v4) :
 * - @type : mappé par catégorie (SportsClub, NGO, EducationalOrganization, etc.)
 * - address : PostalAddress depuis les MORCEAUX (rue déclarée, code postal,
 *   ville, territoire, pays réel — lot 4), plus `geo` si le point est exposé
 * - sameAs : tous les réseaux sociaux fournis
 * - areaServed : City pour le ranking local
 * - identifier : SIRET et RNA (signal de trust)
 * - image : cover_image (fallback logo si pas de cover)
 * - potentialAction : DonateAction — c'est LE cas d'usage de la page
 *   (audit SEO 2026-07-11)
 */
import {
  adresseDeclareeOrganisation,
  TERRITOIRES_OUTRE_MER,
  themeDepuisCategorie,
  type AdresseDeclaree,
  type ThemeKey,
} from "@donoor/utils";
import { formaterNumeroOfficiel, telephoneInternational } from "@donoor/utils";
import type { ChampsAdresseOrganisation } from "@donoor/types";

interface OrgSchemaInput extends ChampsAdresseOrganisation {
  name: string;
  slug: string;
  description: string | null;
  website: string | null;
  full_address: string | null;
  category: string | null;
  rna?: string | null;
  siret?: string | null;
  /** Numéro officiel hors France (Belgique : BCE) — publié une fois vérifié. */
  numero_officiel?: string | null;
  numero_officiel_type?: string | null;
  numero_officiel_verifie_at?: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  /** Pays ISO de l'asso (France à défaut). */
  country?: string | null;
  logo: string | null;
  cover_image: string | null;
  social_links: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    youtube?: string;
    tiktok?: string;
    linkedin?: string;
  } | null;
}

/**
 * Thème → sous-type Schema.org d'Organization (2026-09-26).
 *
 * Uniquement des sous-types VALIDES d'Organization : l'ancienne table visait
 * « SportsClub » (sous-type de LocalBusiness) et des clés (« sport »,
 * « religieuse ») qui n'existaient pas en base — elle ne s'appliquait donc
 * jamais. Quand rien n'est sûr (culte, culture…), on reste sur le générique
 * plutôt que de mentir aux moteurs de recherche.
 */
const SCHEMA_TYPE_BY_THEME: Partial<Record<ThemeKey, string>> = {
  sport: "SportsOrganization",
  education: "EducationalOrganization",
  humanitaire: "NGO",
  solidarite: "NGO",
  ecologie: "NGO",
  animaux: "NGO",
  enfance_jeunesse: "NGO",
};
// ⚠️ PAS d'openingHours dans ce JSON-LD : Schema.org ne reconnaît cette
// propriété que sur LocalBusiness/CivicStructure, jamais sur Organization ni
// ses sous-types émis ici (NGO, MedicalOrganization…) — SemRush la remontait
// en « données structurées invalides » sur chaque asso avec horaires (audit
// 2026-08-05). Les horaires restent affichés dans le HTML de la page
// (section « Horaires », formatOpeningHours local à [slug].vue).

/**
 * Résout le sous-type Schema.org le plus spécifique pour une catégorie.
 * Fallback "Organization" si catégorie absente ou non mappée.
 */
function resolveSchemaType(category: string | null): string {
  return SCHEMA_TYPE_BY_THEME[themeDepuisCategorie(category)] ?? "Organization";
}

/**
 * sameAs : tous les réseaux sociaux fournis + site web.
 * L'ordre (site → facebook → ... → linkedin) est préservé pour un output stable.
 */
function buildSameAs(org: OrgSchemaInput): string[] {
  // normalizeExternalUrl : champs saisis librement — sans protocole, l'URL
  // serait invalide dans le JSON-LD (Google exige des URL absolues).
  const sameAs: string[] = [];
  const website = normalizeExternalUrl(org.website);
  if (website) sameAs.push(website);
  const s = org.social_links;
  if (s) {
    for (const raw of [s.facebook, s.instagram, s.twitter, s.youtube, s.tiktok, s.linkedin]) {
      const url = normalizeExternalUrl(raw);
      if (url) sameAs.push(url);
    }
  }
  return sameAs;
}

/**
 * identifier : SIRET et/ou RNA (hors France : numéro officiel vérifié) = signal
 * de trust (entité légale réelle).
 */
function buildIdentifiers(
  org: OrgSchemaInput,
): Array<{ "@type": string; propertyID: string; value: string }> {
  const identifiers: Array<{ "@type": string; propertyID: string; value: string }> = [];
  if (org.rna) {
    identifiers.push({
      "@type": "PropertyValue",
      propertyID: "RNA",
      value: org.rna,
    });
  }
  // SIRET : seule identité d'une fondation, d'un fonds de dotation ou d'une
  // association de droit local (pas de RNA). Sans lui, ces organismes
  // n'avaient AUCUN identifiant structuré pour Google (Karim 2026-09-16).
  if (org.siret) {
    identifiers.push({
      "@type": "PropertyValue",
      propertyID: "SIRET",
      value: org.siret,
    });
  }
  // Hors France (chantier « autres pays ») : le numéro officiel du pays, une
  // fois VÉRIFIÉ — même règle que l'identité affichée sur la page. Son nom
  // vient du type de la fiche (« BE_BCE » → « BCE »), rien d'écrit par pays.
  const type = org.numero_officiel_type;
  if (type && org.numero_officiel && org.numero_officiel_verifie_at) {
    identifiers.push({
      "@type": "PropertyValue",
      propertyID: type.slice(type.indexOf("_") + 1),
      value: formaterNumeroOfficiel(type, org.numero_officiel),
    });
  }
  return identifiers;
}

/**
 * address : PostalAddress depuis l'adresse DÉCLARÉE (règle partagée
 * `adresseDeclareeOrganisation`, §10 et §17.7.3 du contrat des adresses
 * structurées) — `streetAddress` = rue DÉCLARÉE (texte), `postalCode`,
 * `addressLocality`, `addressRegion` = territoire d'outre-mer (« Martinique »),
 * `addressCountry` = pays RÉEL (fin du « FR » en dur : une asso belge était
 * déclarée française aux moteurs). Sans rue lisible : le texte complet.
 */
function buildAddress(
  org: OrgSchemaInput,
  adresse: AdresseDeclaree | null,
): Record<string, unknown> {
  const territoire = adresse?.territoire
    ? TERRITOIRES_OUTRE_MER.find((t) => t.code === adresse.territoire)?.nom
    : null;
  return {
    "@type": "PostalAddress",
    streetAddress: adresse?.rue || org.full_address,
    ...(adresse?.codePostal ? { postalCode: adresse.codePostal } : {}),
    ...(adresse?.ville ? { addressLocality: adresse.ville } : {}),
    ...(territoire ? { addressRegion: territoire } : {}),
    addressCountry: adresse?.pays || org.country || "FR",
  };
}

/**
 * geo : GeoCoordinates quand l'API expose le point (elle ne l'expose que s'il
 * décrit l'adresse actuelle, §6) — la fiche est placée sur la carte sans
 * que Google ait à géocoder le texte.
 *
 * ⚠️ Schema.org ne connaît `geo` que sur un `Place`, jamais sur une
 * Organization (même piège qu'`openingHours`, audit 2026-08-05) : le point
 * est donc émis dans `location` (Place : adresse + coordonnées).
 */
function buildGeo(org: OrgSchemaInput): Record<string, unknown> | null {
  const { geo_latitude: latitude, geo_longitude: longitude } = org;
  if (typeof latitude !== "number" || typeof longitude !== "number") return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { "@type": "GeoCoordinates", latitude, longitude };
}

/**
 * contactPoint : email et/ou téléphone du service client.
 */
function buildContactPoint(org: OrgSchemaInput): Record<string, unknown> {
  return {
    "@type": "ContactPoint",
    contactType: "customer service",
    ...(org.contact_email ? { email: org.contact_email } : {}),
    // Forme internationale (E.164), lue dans le pays de l'asso : c'est ce
    // que schema.org attend, et le seul format composable depuis partout.
    ...(org.contact_phone
      ? { telephone: telephoneInternational(org.contact_phone, org.country || "FR") ?? org.contact_phone }
      : {}),
    areaServed: org.country || "FR",
    availableLanguage: ["French"],
  };
}

/**
 * Construit le payload JSON-LD enrichi.
 * Retourne null si les données minimum (name) ne sont pas là.
 */
export function buildOrganizationSchema(
  org: OrgSchemaInput | null | undefined,
  ctx: { canonicalUrl: string; assetsBaseUrl: string; description: string },
): Record<string, unknown> | null {
  if (!org || !org.name) return null;

  // Composable appelé depuis le setup de [slug].vue (lot i18n O3) : `t` suit
  // la langue de la page à l'instant de l'appel.
  const { t } = useI18n();

  const adresse = adresseDeclareeOrganisation(org.full_address, org);
  const city = adresse?.ville ?? null;
  const geo = buildGeo(org);
  const sameAs = buildSameAs(org);
  const identifiers = buildIdentifiers(org);

  const payload: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": resolveSchemaType(org.category),
    // @id stable par association (audit SEO 2026-07-11) : sans identifiant de
    // nœud, Google ne peut pas consolider l'entité à travers ses signaux de
    // découverte (page Donoor, site propre de l'asso, annuaires).
    "@id": `${ctx.canonicalUrl}#organization`,
    name: org.name,
    url: ctx.canonicalUrl,
    description: ctx.description,
  };

  if (org.logo) payload.logo = `${ctx.assetsBaseUrl}/${org.logo}`;
  // image : cover en priorité, sinon logo (une Organization sans image du tout
  // n'a aucune chance de Knowledge Panel avec visuel — audit SEO 2026-07-11).
  if (org.cover_image) payload.image = `${ctx.assetsBaseUrl}/${org.cover_image}`;
  else if (org.logo) payload.image = `${ctx.assetsBaseUrl}/${org.logo}`;
  if (org.full_address) payload.address = buildAddress(org, adresse);
  if (geo) {
    payload.location = {
      "@type": "Place",
      ...(payload.address ? { address: payload.address } : {}),
      geo,
    };
  }
  if (org.contact_email || org.contact_phone) payload.contactPoint = buildContactPoint(org);
  if (sameAs.length > 0) payload.sameAs = sameAs;
  if (city) payload.areaServed = { "@type": "City", name: city };
  if (identifiers.length > 0) payload.identifier = identifiers;

  // Pas de nonprofitStatus : Schema.org n'a aucun enum NonprofitType pour la
  // France (NonprofitANBI = Pays-Bas, Nonprofit501c3 = USA). Le RNA passe déjà
  // via identifier (PropertyValue), ce qui suffit à qualifier l'asso loi 1901.

  // DonateAction : signale aux moteurs (et LLMs) que cette page sert à donner
  // à CETTE asso — le cas d'usage exact de la page (audit SEO 2026-07-11).
  payload.potentialAction = {
    "@type": "DonateAction",
    name: t("association.schema.faireUnDon", { nom: org.name }),
    target: ctx.canonicalUrl,
    recipient: {
      "@type": resolveSchemaType(org.category),
      name: org.name,
    },
  };

  return payload;
}
